import { BybDialog } from "@Components/game/common";
import { BootcampCompletionContent } from "@Components/game/session-results/bootcamp/bootcamp-completion";
import { BootcampChallengeStep } from "@Constants/bootcamp";
import useI18n from "@i18n";
import { Button, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";

interface BootcampCompletionDialogProps {
  challengeStep: BootcampChallengeStep;
  metricScore?: number;
  metricTarget?: number;
  completedSteps: number;
  failed?: boolean;
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  onRetry: () => void;
}

const BootcampCompletionDialog = ({
  challengeStep,
  metricScore,
  metricTarget,
  completedSteps,
  failed = false,
  open,
  onClose,
  onContinue,
  onRetry,
}: BootcampCompletionDialogProps) => {
  const { i18n } = useI18n();
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setCompleteModalOpen(open);
  }, [open]);

  const closeWithAction = (action: () => void) => {
    pendingActionRef.current = action;
    setCompleteModalOpen(false);
  };

  const handleClose = () => closeWithAction(onClose);
  const handleContinue = () => closeWithAction(failed ? onRetry : onContinue);

  const handleExited = () => {
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  };

  return (
    <BybDialog
      name="bootcamp-completion-dialog"
      open={completeModalOpen}
      onClose={handleClose}
      showCloseButton
      transparent
      width="50vw"
      sx={{ "& .MuiDialog-paper": { minWidth: 0 } }}
      TransitionProps={{ onExited: handleExited }}
      dialogActions={
        <Stack width="100%" paddingX={3} paddingBottom={2} boxSizing="border-box">
          <Button data-shape="pill" onClick={handleContinue} fullWidth sx={{ minHeight: 64 }}>
            {i18n(
              failed
                ? "session-results.bootcamp.play-again"
                : challengeStep === BootcampChallengeStep.FINAL
                  ? "session-results.bootcamp.see-full-results"
                  : "session-results.bootcamp.continue"
            )}
          </Button>
        </Stack>
      }
    >
      <Stack paddingX={5} paddingTop={5} paddingBottom={1}>
        <BootcampCompletionContent
          challengeStep={challengeStep}
          metricScore={metricScore}
          metricTarget={metricTarget}
          completedSteps={completedSteps}
          failed={failed}
        />
      </Stack>
    </BybDialog>
  );
};

export default BootcampCompletionDialog;
